import React, { createContext, useContext, useState, useEffect } from 'react';
import { teamAPI } from '../services/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Permission definitions by role
const PERMISSIONS = {
  Admin: {
    canDeleteTask: true,
    canDeleteNote: true,
    canDeleteMessage: true,
    canDeleteClient: true,
    canDeleteInvoice: true,
    canDeleteDocument: true,
    canManageUsers: true,
    canEditSettings: true,
  },
  Manager: {
    canDeleteTask: true,
    canDeleteNote: true,
    canDeleteMessage: true,
    canDeleteClient: false,
    canDeleteInvoice: true,
    canDeleteDocument: true,
    canManageUsers: false,
    canEditSettings: false,
  },
  Accountant: {
    canDeleteTask: true,
    canDeleteNote: true,
    canDeleteMessage: true,
    canDeleteClient: false,
    canDeleteInvoice: true,
    canDeleteDocument: true,
    canManageUsers: false,
    canEditSettings: false,
  },
  Staff: {
    canDeleteTask: false,
    canDeleteNote: false,
    canDeleteMessage: false,
    canDeleteClient: false,
    canDeleteInvoice: false,
    canDeleteDocument: false,
    canManageUsers: false,
    canEditSettings: false,
  },
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for stored user session
    const storedUser = localStorage.getItem('mantra_user');
    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser));
      } catch (e) {
        localStorage.removeItem('mantra_user');
      }
    }
    setLoading(false);
  }, []);

  const login = async (email, password) => {
    const response = await teamAPI.login({ email, password });
    if (response.data.success) {
      const userData = response.data.user;
      setUser(userData);
      localStorage.setItem('mantra_user', JSON.stringify(userData));
      return { success: true, user: userData };
    }
    throw new Error('Login failed');
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('mantra_user');
  };

  const hasPermission = (permission) => {
    if (!user) return false;
    const role = user.role || 'Staff';
    const rolePermissions = PERMISSIONS[role] || PERMISSIONS.Staff;
    return rolePermissions[permission] || false;
  };

  const isAdmin = () => user?.role === 'Admin';
  const isManager = () => user?.role === 'Manager';
  const isAccountant = () => user?.role === 'Accountant';
  const isStaff = () => user?.role === 'Staff';

  const value = {
    user,
    loading,
    login,
    logout,
    hasPermission,
    isAdmin,
    isManager,
    isAccountant,
    isStaff,
    isAuthenticated: !!user,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
